import { useCallback, useEffect, useState } from "react";
import { api, type HistorySearch, type HistoryWatch } from "../api";
import { EmptyState } from "../components/Feedback";
import { formatDateTime, formatDuration } from "../utils";

interface HistoryScreenProps {
  showSuccess: (message: string) => void;
  showError: (error: unknown) => void;
}

export function HistoryScreen({ showSuccess, showError }: HistoryScreenProps) {
  const [days, setDays] = useState<1 | 7 | 30>(1);
  const [tab, setTab] = useState<"watch" | "search">("watch");
  const [watch, setWatch] = useState<HistoryWatch[]>([]);
  const [searches, setSearches] = useState<HistorySearch[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.history(days);
      setWatch(result.watch);
      setSearches(result.search);
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  }, [days, showError]);

  useEffect(() => { void load(); }, [load]);

  async function blockKeyword(search: HistorySearch) {
    setBusyId(search.id);
    try {
      await api.blockKeyword(search.query, "CONTAINS");
      showSuccess(`Đã chặn từ khóa “${search.query}”.`);
    } catch (error) {
      showError(error);
    } finally {
      setBusyId(null);
    }
  }

  const totalWatchSeconds = watch.reduce((sum, item) => sum + item.watch_seconds, 0);
  return (
    <div className="page-stack">
      <header className="page-header">
        <div><p className="eyebrow">ACTIVITY</p><h1>Lịch sử hoạt động</h1><p>Lịch sử riêng của KidTube, không cần tài khoản Google trên TV.</p></div>
        <div className="range-picker" aria-label="Khoảng thời gian">
          {([1, 7, 30] as const).map((value) => (
            <button type="button" key={value} className={days === value ? "active" : ""} onClick={() => setDays(value)}>{value === 1 ? "Hôm nay" : `${value} ngày`}</button>
          ))}
        </div>
      </header>

      <section className="summary-grid history-summary">
        <article className="summary-card accent-blue"><span className="summary-icon">◷</span><div><strong>{formatDuration(totalWatchSeconds)}</strong><small>Tổng thời gian xem</small></div></article>
        <article className="summary-card accent-violet"><span className="summary-icon">▶</span><div><strong>{watch.length}</strong><small>Lượt xem</small></div></article>
        <article className="summary-card accent-green"><span className="summary-icon">⌕</span><div><strong>{searches.length}</strong><small>Lượt tìm kiếm</small></div></article>
      </section>

      <div className="segmented-tabs history-tabs" role="tablist">
        <button type="button" className={tab === "watch" ? "active" : ""} onClick={() => setTab("watch")}>Video đã xem <span>{watch.length}</span></button>
        <button type="button" className={tab === "search" ? "active" : ""} onClick={() => setTab("search")}>Từ khóa tìm kiếm <span>{searches.length}</span></button>
      </div>

      {loading ? <div className="skeleton skeleton-section" /> : tab === "watch" ? (
        watch.length === 0 ? <EmptyState icon="◷" title="Chưa có lịch sử xem" description="Lịch sử xuất hiện sau khi TV gửi playback report đầu tiên." /> : (
          <div className="history-list">
            {watch.map((item) => (
              <article className="history-row" key={item.id}>
                <span className="history-dot" />
                <div className="history-main"><strong>{item.title_snapshot}</strong><small>{item.channel_name_snapshot} · {item.device_name}</small></div>
                <div className="history-metrics"><strong>{formatDuration(item.watch_seconds)}</strong><small>{Math.round(item.completion_percent)}% hoàn thành</small></div>
                <div className="history-time"><strong>{formatDateTime(item.started_at)}</strong><small>{item.parent_stopped === 1 ? "Parent đã dừng" : item.ended_at === null ? "Chưa kết thúc" : "Đã kết thúc"}</small></div>
              </article>
            ))}
          </div>
        )
      ) : searches.length === 0 ? (
        <EmptyState icon="⌕" title="Chưa có lịch sử tìm kiếm" description="Mỗi tìm kiếm từ TV sẽ được lưu riêng trong KidTube." />
      ) : (
        <div className="history-list">
          {searches.map((item) => (
            <article className="history-row search-history-row" key={item.id}>
              <span className="rule-icon">⌕</span>
              <div className="history-main"><strong>{item.query}</strong><small>{item.result_count} kết quả phù hợp · {item.device_name}</small></div>
              <div className="history-time"><strong>{formatDateTime(item.searched_at)}</strong></div>
              <button type="button" className="secondary-button danger-text" onClick={() => void blockKeyword(item)} disabled={busyId === item.id}>Chặn từ khóa</button>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
